import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import ShareCapitalForm, { emptyShareCapital, shareCapitalPayload } from "@/components/ShareCapitalForm";

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const Posted = ({ rec }) => (
  <div className="space-y-3 text-sm" data-testid="sc-posted">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
      <div>Incorporation: <b>{rec.incorporation_date}</b></div>
      <div>Authorised: <b>{rec.authorised_capital ? fmt(rec.authorised_capital) : "—"}</b></div>
      <div>Paid-up: <b>{fmt(rec.paid_up_capital)}</b></div>
      <div>Face value: <b>₹{rec.face_value}</b></div>
    </div>
    <table className="w-full text-xs border">
      <thead className="bg-muted/50"><tr><th className="text-left p-2">Shareholder</th><th className="text-left p-2">PAN</th><th className="text-right p-2">Shares</th><th className="text-right p-2">Amount</th><th className="text-right p-2">Holding</th></tr></thead>
      <tbody>{rec.shareholders.map((s, i) => (
        <tr key={i} className="border-t"><td className="p-2">{s.name}</td><td className="p-2 font-mono">{s.pan || "—"}</td><td className="p-2 text-right">{s.shares}</td><td className="p-2 text-right">{fmt(s.amount)}</td><td className="p-2 text-right">{((s.amount / rec.paid_up_capital) * 100).toFixed(2)}%</td></tr>
      ))}</tbody>
    </table>
    <div className="text-xs text-muted-foreground">
      Allotment JV <b className="font-mono" data-testid="sc-allotment-vno">{rec.allotment_voucher?.number}</b>
      {rec.receipt_voucher && <> · Bank receipt JV <b className="font-mono" data-testid="sc-receipt-vno">{rec.receipt_voucher.number}</b> ({rec.bank_account})</>}
    </div>
  </div>
);

export default function ShareholdersTab() {
  const [state, setState] = useState(null);
  const [form, setForm] = useState(emptyShareCapital());
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { setState((await api.get("/share-capital")).data); }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const submit = async () => {
    setBusy(true);
    try { await api.post("/share-capital", shareCapitalPayload(form)); toast.success("Share capital entries posted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Posting failed"); }
    finally { setBusy(false); }
  };

  if (!state) return null;
  return (
    <Card className="p-4 rounded-sm space-y-4" data-testid="shareholders-tab">
      <div>
        <div className="font-medium">Share capital &amp; shareholders</div>
        <p className="text-xs text-muted-foreground">Paid-up capital at formation and the allotment entries.</p>
      </div>
      {!state.applicable && <div className="text-xs text-muted-foreground" data-testid="sc-not-applicable">Applies to companies (Pvt Ltd / Ltd / OPC) only.</div>}
      {state.applicable && state.record && <Posted rec={state.record} />}
      {state.applicable && !state.record && (
        <>
          <ShareCapitalForm value={form} onChange={setForm} />
          <Button onClick={submit} disabled={busy} className="rounded-sm" data-testid="sc-post-btn">{busy ? "Posting…" : "Save & post entries"}</Button>
        </>
      )}
    </Card>
  );
}
